import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";

const norm = (s) => (s || "").toLocaleLowerCase("es").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Selector propio buscable (single / multi) con el lenguaje visual del onboarding aprobado.
export const CaSelect = ({ options, value, onChange, multiple = false, searchable = false, placeholder = "Selecciona una opción…", invalid = false, testId, searchPlaceholder = "Buscar…", countLabel }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const box = useRef(null);
  const search = useRef(null);
  const selected = multiple ? (value || []) : value;

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    if (search.current) search.current.focus();
    return () => { document.removeEventListener("pointerdown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const visible = useMemo(() => (q ? options.filter((o) => norm(o.label).includes(norm(q))) : options), [q, options]);
  const isSel = (v) => (multiple ? selected.includes(v) : selected === v);
  const labelOf = (v) => options.find((o) => o.value === v)?.label || v;

  const pick = (v) => {
    if (multiple) { onChange(isSel(v) ? selected.filter((x) => x !== v) : [...selected, v]); search.current?.focus(); }
    else { onChange(v); setOpen(false); setQ(""); }
  };

  let text = placeholder, ph = true;
  if (multiple && selected.length) { text = countLabel ? countLabel(selected.length) : `${selected.length} seleccionados`; ph = false; }
  if (!multiple && selected) { text = labelOf(selected); ph = false; }

  const onMenuKey = (e) => {
    const items = [...box.current.querySelectorAll(".ca-sel-option")];
    const i = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown") { e.preventDefault(); items[Math.min(items.length - 1, i + 1)]?.focus(); }
    if (e.key === "ArrowUp") { e.preventDefault(); items[Math.max(0, i - 1)]?.focus(); }
  };

  return (
    <div ref={box} className={`ca-sel ${open ? "is-open" : ""} ${invalid ? "is-invalid" : ""}`} data-testid={testId}>
      <button type="button" className="ca-sel-trigger" aria-haspopup="listbox" aria-expanded={open} data-testid={`${testId}-trigger`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (["ArrowDown", "Enter", " "].includes(e.key) && !open) { e.preventDefault(); setOpen(true); } }}>
        <span className={`ca-sel-value ${ph ? "is-placeholder" : ""}`}>{text}</span>
        <svg viewBox="0 0 20 20" aria-hidden="true" className="ca-sel-chevron"><path d="m5 7 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <div className="ca-sel-menu" role="listbox" aria-multiselectable={multiple} onKeyDown={onMenuKey}>
          {searchable && (
            <input ref={search} type="search" className="ca-sel-search" placeholder={searchPlaceholder} aria-label={searchPlaceholder}
              value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search`} />
          )}
          <div>
            {visible.map((o) => (
              <button type="button" key={o.value} role="option" aria-selected={isSel(o.value)} className="ca-sel-option"
                onClick={() => pick(o.value)} data-testid={`${testId}-opt-${o.value}`}>{o.label}</button>
            ))}
            {visible.length === 0 && <div className="ca-sel-empty">No hay resultados</div>}
          </div>
          {multiple && <button type="button" className="ca-sel-done" onClick={() => setOpen(false)} data-testid={`${testId}-done`}>Listo</button>}
        </div>
      )}
      {multiple && selected.length > 0 && (
        <div className="ca-sel-tags" data-testid={`${testId}-tags`}>
          {selected.map((v) => (
            <span key={v} className="ca-sel-tag" data-testid={`${testId}-tag-${v}`}>
              {labelOf(v)}
              <button type="button" aria-label={`Quitar ${labelOf(v)}`} onClick={() => onChange(selected.filter((x) => x !== v))} data-testid={`${testId}-remove-${v}`}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
